import React, { useState, useMemo } from 'react';
import { Card, LoadingSpinner, Badge } from '../UI';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, Clock, MapPin, Star, AlertTriangle, XCircle, Coffee, TrendingUp, UserX, FileEdit, CheckCircle, Loader2, Ban, RotateCcw } from 'lucide-react';
import { attendanceAPI } from '../../services';

const AttendanceCalendar = ({ attendance = [], holidays = [], regularizations = [], revokes = [], loading = false, workingDays = [1, 2, 3, 4, 5], weekoffConfig = null, employeeStartDate = null, shiftInfo = null, onRefresh = null, onRegularizationApplied = null, onRevokeApplied = null, month = null, year = null, onMonthChange = null }) => {
  // Use weekoffConfig if provided, otherwise fall back to workingDays for backward compatibility
  const effectiveWeekoffConfig = weekoffConfig || { weekoffType: 'fixed', workingDays, rotationWeekoffs: [] };

  // Parse employee start date
  const startDate = useMemo(() => {
    if (!employeeStartDate) return null;
    const date = new Date(employeeStartDate);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [employeeStartDate]);
  // month prop is 1-indexed; internal state is 0-indexed
  const [currentMonth, setCurrentMonth] = useState(month ? month - 1 : new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(year || new Date().getFullYear());

  // Sync internal state when controlled props change (e.g. parent navigation)
  React.useEffect(() => {
    if (month != null) setCurrentMonth(month - 1);
  }, [month]);
  React.useEffect(() => {
    if (year != null) setCurrentYear(year);
  }, [year]);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Regularization state
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [regularizationLoading, setRegularizationLoading] = useState(false);
  const [regularizationError, setRegularizationError] = useState('');
  const [regularizationSuccess, setRegularizationSuccess] = useState('');
  const [regularizationForm, setRegularizationForm] = useState({
    date: '',
    type: 'forgot_punch',
    requestedPunchIn: '09:00',
    requestedPunchOut: '18:00',
    reason: ''
  });

  const regularizationTypes = [
    { value: 'forgot_punch', label: 'Forgot to Punch' },
    { value: 'missing_punch_in', label: 'Missing Punch In' },
    { value: 'missing_punch_out', label: 'Missing Punch Out' },
    { value: 'wrong_location', label: 'Wrong Location' },
    { value: 'system_error', label: 'System Error' },
    { value: 'other', label: 'Other' }
  ];

  // Revoke state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeError, setRevokeError] = useState('');
  const [revokeSuccess, setRevokeSuccess] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeAttendanceId, setRevokeAttendanceId] = useState(null);

  const getRevokeForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return revokes.find(r => {
      const d = new Date(r.date);
      const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return s === dateStr;
    });
  };

  const openRevokeModal = (attendanceId) => {
    setRevokeAttendanceId(attendanceId);
    setRevokeReason('');
    setRevokeError('');
    setRevokeSuccess('');
    setShowRevokeModal(true);
  };

  const handleRevokeSubmit = async (e) => {
    e.preventDefault();
    if (!revokeReason.trim()) { setRevokeError('Please provide a reason'); return; }
    setRevokeLoading(true);
    setRevokeError('');
    try {
      await attendanceAPI.requestRevoke({ attendanceId: revokeAttendanceId, reason: revokeReason.trim() });
      setRevokeSuccess('Revoke request submitted. Awaiting manager approval.');
      setTimeout(() => {
        setShowRevokeModal(false);
        setRevokeSuccess('');
        if (onRevokeApplied) onRevokeApplied();
      }, 1500);
    } catch (err) {
      setRevokeError(err.message || 'Failed to submit revoke request');
    } finally {
      setRevokeLoading(false);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Helper to get week of month (1-5)
  const getWeekOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfMonth = date.getDate();
    const firstDayWeekday = firstDay.getDay();
    return Math.ceil((dayOfMonth + firstDayWeekday) / 7);
  };

  // Helper to check if a date is a weekoff
  const isWeekoffDay = (date) => {
    const dayOfWeek = date.getDay();
    
    if (effectiveWeekoffConfig.weekoffType === 'fixed') {
      return !effectiveWeekoffConfig.workingDays.includes(dayOfWeek);
    } else if (effectiveWeekoffConfig.weekoffType === 'rotation') {
      const weekNumber = getWeekOfMonth(date);
      const weekConfig = effectiveWeekoffConfig.rotationWeekoffs?.find(r => r.weekNumber === weekNumber);
      if (weekConfig) {
        return weekConfig.weekoffDays.includes(dayOfWeek);
      }
      // Fallback to last week's pattern if current week not defined
      if (effectiveWeekoffConfig.rotationWeekoffs && effectiveWeekoffConfig.rotationWeekoffs.length > 0) {
        const lastWeekConfig = effectiveWeekoffConfig.rotationWeekoffs[effectiveWeekoffConfig.rotationWeekoffs.length - 1];
        return lastWeekConfig.weekoffDays.includes(dayOfWeek);
      }
    }
    return false;
  };

  // Helper to check if a day is a weekend/off day (backward compatible)
  const isOffDay = (dayOfWeek) => {
    // For backward compatibility, also check using day of week directly
    if (typeof dayOfWeek === 'number') {
      return !effectiveWeekoffConfig.workingDays.includes(dayOfWeek);
    }
    // If passed a date object
    return isWeekoffDay(dayOfWeek);
  };

  const getStatusColor = (status, isWeekendWork = false, missedPunchOut = false) => {
    if (isWeekendWork) return 'bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-300';
    // If missed punch out, show as red (absent)
    if (missedPunchOut) return 'bg-red-500 hover:bg-red-600';
    const colors = {
      present: 'bg-green-500 hover:bg-green-600',
      absent: 'bg-red-500 hover:bg-red-600',
      half_day: 'bg-amber-500 hover:bg-amber-600',
      leave: 'bg-blue-500 hover:bg-blue-600',
      weekend: 'bg-slate-200 hover:bg-slate-300',
      holiday: 'bg-purple-500 hover:bg-purple-600'
    };
    return colors[status] || 'bg-gray-200 hover:bg-gray-300';
  };

  const getStatusLabel = (status, missedPunchOut = false) => {
    // If missed punch out, show specific label
    if (missedPunchOut) return 'Missed Punch Out';
    const labels = {
      present: 'Present',
      absent: 'Absent',
      half_day: 'Half Day',
      leave: 'Leave',
      weekend: 'Weekend',
      holiday: 'Holiday'
    };
    return labels[status] || 'Unknown';
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getAttendanceForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return attendance.find(a => formatDate(a.date) === dateStr);
  };

  const getHolidayForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays.find(h => formatDate(h.date) === dateStr);
  };

  // Get regularization request for a specific date
  const getRegularizationForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return regularizations.find(r => formatDate(r.date) === dateStr);
  };

  // Calculate extra work days (weekend work)
  const extraWorkDays = useMemo(() => {
    return attendance.filter(a => {
      const date = new Date(a.date);
      const dayOfWeek = date.getDay();
      return isOffDay(dayOfWeek) && (a.status === 'present' || a.status === 'half_day');
    });
  }, [attendance, workingDays]);

  const handlePreviousMonth = () => {
    setSelectedDate(null);
    let newMonth, newYear;
    if (currentMonth === 0) {
      newMonth = 11;
      newYear = currentYear - 1;
    } else {
      newMonth = currentMonth - 1;
      newYear = currentYear;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    if (onMonthChange) onMonthChange(newMonth + 1, newYear);
  };

  const handleNextMonth = () => {
    setSelectedDate(null);
    let newMonth, newYear;
    if (currentMonth === 11) {
      newMonth = 0;
      newYear = currentYear + 1;
    } else {
      newMonth = currentMonth + 1;
      newYear = currentYear;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    if (onMonthChange) onMonthChange(newMonth + 1, newYear);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(null);
    if (onMonthChange) onMonthChange(today.getMonth() + 1, today.getFullYear());
  };

  const handleDateClick = (day, attendanceData, isWeekend, holidayData, isWeekendWork, isFutureDate, isBeforeStartDate, isMissedPunchOut) => {
    setSelectedDate({ day, attendanceData, isWeekend, holidayData, isWeekendWork, isFutureDate, isBeforeStartDate, isMissedPunchOut });
  };

  // Open regularization modal for a specific date
  const openRegularizationModal = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setRegularizationForm({
      date: dateStr,
      type: 'forgot_punch',
      requestedPunchIn: '09:00',
      requestedPunchOut: '18:00',
      reason: ''
    });
    setRegularizationError('');
    setRegularizationSuccess('');
    setShowRegularizationModal(true);
  };

  // Handle regularization form submit
  const handleRegularizationSubmit = async (e) => {
    e.preventDefault();
    setRegularizationError('');
    setRegularizationLoading(true);

    try {
      if (!regularizationForm.reason) {
        throw new Error('Please provide a reason for regularization');
      }

      const { requestedPunchIn: pinTime, requestedPunchOut: poutTime, date } = regularizationForm;

      // Validate punch out is after punch in when both are provided
      if (pinTime && poutTime) {
        const [pinH, pinM] = pinTime.split(':').map(Number);
        const [poutH, poutM] = poutTime.split(':').map(Number);
        const pinMinutes = pinH * 60 + pinM;
        const poutMinutes = poutH * 60 + poutM;
        if (poutMinutes <= pinMinutes) {
          throw new Error('Punch Out time must be after Punch In time');
        }
        const workMinutes = poutMinutes - pinMinutes;
        if (workMinutes < 30) {
          throw new Error('Work duration must be at least 30 minutes');
        }
      }

      const submitData = {
        date,
        type: regularizationForm.type,
        reason: regularizationForm.reason
      };

      // Send as explicit IST datetime strings (+05:30) so the server always
      // interprets the time correctly regardless of where it runs.
      if (pinTime) {
        submitData.requestedPunchIn = `${date}T${pinTime}:00+05:30`;
      }
      if (poutTime) {
        submitData.requestedPunchOut = `${date}T${poutTime}:00+05:30`;
      }

      await attendanceAPI.applyRegularization(submitData);
      
      setRegularizationSuccess('Regularization request submitted successfully!');
      setTimeout(() => {
        setShowRegularizationModal(false);
        setRegularizationSuccess('');
        if (onRefresh) onRefresh();
        if (onRegularizationApplied) onRegularizationApplied();
      }, 1500);
    } catch (err) {
      setRegularizationError(err.message || 'Failed to submit request');
    } finally {
      setRegularizationLoading(false);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const today = new Date();
    const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;

    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Day headers
    days.push(
      <div key="headers" className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map((day, index) => (
          <div
            key={day}
            className={`text-center text-xs font-bold py-2 rounded-lg ${
              isOffDay(index) ? 'text-red-500 bg-red-50' : 'text-slate-600 bg-slate-50'
            }`}
          >
            {day}
          </div>
        ))}
      </div>
    );

    // Empty cells for days before month starts
    const cells = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <div key={`empty-${i}`} className="aspect-square"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const attendanceData = getAttendanceForDate(day);
      const holidayData = getHolidayForDate(day);
      const isToday = isCurrentMonth && today.getDate() === day;
      const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
      const isWeekend = isOffDay(dayOfWeek);
      const dateObj = new Date(currentYear, currentMonth, day);
      dateObj.setHours(0, 0, 0, 0);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const isPastDate = dateObj < todayStart;
      const isFutureDate = dateObj > todayStart;
      const isBeforeStartDate = startDate && dateObj < startDate;
      const isSelected = selectedDate?.day === day;
      
      // Check if worked on weekend - use effectiveStatus if available
      const displayStatus = attendanceData?.effectiveStatus || attendanceData?.status;
      const isMissedPunchOut = attendanceData?.missedPunchOut || false;
      const isWeekendWork = isWeekend && attendanceData && !isMissedPunchOut && (displayStatus === 'present' || displayStatus === 'half_day');

      // Determine what to show
      let cellClass = '';
      let textClass = 'text-slate-700';
      let showStatus = null;

      if (isBeforeStartDate) {
        // Date is before employee joined - show as inactive
        cellClass = 'bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-200';
        textClass = 'text-slate-300';
        showStatus = 'before_joining';
      } else if (holidayData) {
        cellClass = 'bg-purple-500 hover:bg-purple-600';
        textClass = 'text-white';
        showStatus = 'holiday';
      } else if (isWeekendWork) {
        // Weekend but worked - show as extra work with special styling
        cellClass = 'bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-300 ring-offset-1';
        textClass = 'text-white';
        showStatus = 'extra_work';
      } else if (attendanceData) {
        cellClass = getStatusColor(displayStatus, false, isMissedPunchOut);
        textClass = 'text-white';
        showStatus = isMissedPunchOut ? 'missed_punchout' : displayStatus;
      } else if (isWeekend) {
        // Weekend without work - just show as weekend, NOT absent
        cellClass = 'bg-slate-100 hover:bg-slate-200 border border-slate-200';
        textClass = 'text-slate-400';
        showStatus = 'weekend';
      } else if (isPastDate) {
        // Past working day without attendance - show as absent
        cellClass = 'bg-red-100 hover:bg-red-200 border border-red-200';
        textClass = 'text-red-600';
        showStatus = 'absent';
      } else {
        cellClass = 'bg-white hover:bg-slate-50 border border-slate-200';
      }

      cells.push(
        <div
          key={day}
          onClick={() => handleDateClick(day, attendanceData, isWeekend, holidayData, isWeekendWork, isFutureDate, isBeforeStartDate, isMissedPunchOut)}
          className={`aspect-square relative cursor-pointer transition-all duration-200 rounded-lg overflow-hidden ${
            isToday ? 'ring-2 ring-indigo-500 ring-offset-2' : ''
          } ${isSelected ? 'ring-2 ring-indigo-400 scale-105 shadow-lg z-10' : 'hover:scale-102'}`}
        >
          <div className={`w-full h-full flex flex-col items-center justify-center ${cellClass} ${textClass}`}>
            <span className="text-sm font-bold">{day}</span>
            
            {/* Status indicator */}
            {showStatus === 'before_joining' && (
              <span className="text-[9px] font-medium mt-0.5">—</span>
            )}
            {showStatus === 'extra_work' && (
              <span className="text-[10px] font-medium flex items-center gap-0.5 mt-0.5">
                <TrendingUp className="w-2.5 h-2.5" />
                Extra
              </span>
            )}
            {showStatus === 'holiday' && (
              <Star className="w-3 h-3 mt-0.5" />
            )}
            {showStatus === 'weekend' && (
              <span className="text-[9px] font-medium mt-0.5">OFF</span>
            )}
            {showStatus === 'present' && (
              <span className="text-[10px] font-medium mt-0.5 flex items-center gap-0.5">
                {attendanceData?.isLateArrival && <Clock className="w-2.5 h-2.5 text-orange-200" />}
                {attendanceData?.workDuration ? `${Math.floor(attendanceData.workDuration / 60)}h` : '✓'}
              </span>
            )}
            {showStatus === 'half_day' && (
              <span className="text-[10px] font-medium mt-0.5 flex items-center gap-0.5">
                {attendanceData?.isLateArrival && <Clock className="w-2.5 h-2.5" />}
                ½
              </span>
            )}
            {showStatus === 'leave' && (
              <Coffee className="w-3 h-3 mt-0.5" />
            )}
            {showStatus === 'absent' && !isWeekend && !isMissedPunchOut && (
              <XCircle className="w-3 h-3 mt-0.5" />
            )}
            {showStatus === 'missed_punchout' && (
              <span className="text-[8px] font-medium mt-0.5">NO OUT</span>
            )}
            
            {/* Late arrival indicator badge - show in corner */}
            {attendanceData?.isLateArrival && (showStatus === 'present' || showStatus === 'extra_work') && (
              <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-orange-400 rounded-full" title="Late arrival" />
            )}
          </div>
        </div>
      );
    }

    days.push(
      <div key="calendar-grid" className="grid grid-cols-7 gap-1">
        {cells}
      </div>
    );

    return days;
  };

  const renderDetailsPanel = () => {
    if (!selectedDate) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6">
          <CalendarIcon className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg font-medium text-center">Select a date</p>
          <p className="text-sm text-center mt-2">Click on any date to view details</p>
        </div>
      );
    }

    const { day, attendanceData, isWeekend, holidayData, isWeekendWork, isFutureDate, isBeforeStartDate, isMissedPunchOut } = selectedDate;
    const displayStatus = attendanceData?.effectiveStatus || attendanceData?.status;
    const hasMissedPunchOut = attendanceData?.missedPunchOut || isMissedPunchOut;

    return (
      <div className="h-full flex flex-col">
        {/* Panel Header */}
        <div className={`p-4 flex items-center justify-between ${
          isBeforeStartDate ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
          holidayData ? 'bg-gradient-to-r from-purple-600 to-purple-700' :
          isWeekendWork ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' :
          hasMissedPunchOut ? 'bg-gradient-to-r from-red-600 to-red-700' :
          displayStatus === 'present' ? 'bg-gradient-to-r from-green-600 to-green-700' :
          displayStatus === 'absent' ? 'bg-gradient-to-r from-red-600 to-red-700' :
          displayStatus === 'half_day' ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
          displayStatus === 'leave' ? 'bg-gradient-to-r from-blue-600 to-blue-700' :
          isWeekend ? 'bg-gradient-to-r from-slate-500 to-slate-600' :
          isFutureDate ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
          'bg-gradient-to-r from-red-600 to-red-700'
        } text-white`}>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold truncate">{monthNames[currentMonth]} {day}</h3>
            <p className="text-xs opacity-80 truncate">
              {new Date(currentYear, currentMonth, day).toLocaleDateString('en-US', { weekday: 'long' })}
            </p>
          </div>
          <button
            onClick={() => setSelectedDate(null)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Holiday Info */}
          {holidayData && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Holiday</p>
                  <p className="text-base font-bold text-purple-900 mt-0.5 truncate">{holidayData.name}</p>
                  {holidayData.type && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-purple-200 text-purple-800 text-xs rounded-full capitalize">
                      {holidayData.type}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Weekend Work Badge */}
          {isWeekendWork && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Extra Work Day</p>
                  <p className="text-sm font-semibold text-emerald-900 mt-0.5">Worked on Weekend</p>
                </div>
              </div>
            </div>
          )}

          {attendanceData ? (
            <>
              {/* Status */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</span>
                  <Badge className={`text-white text-xs px-2.5 py-1 ${
                    hasMissedPunchOut ? 'bg-red-500' :
                    displayStatus === 'present' ? 'bg-green-500' : 
                    displayStatus === 'absent' ? 'bg-red-500' : 
                    displayStatus === 'half_day' ? 'bg-amber-500' : 
                    'bg-blue-500'
                  }`}>
                    {getStatusLabel(displayStatus, hasMissedPunchOut)}
                  </Badge>
                </div>
                {hasMissedPunchOut && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <XCircle className="w-3 h-3" />
                    You punched in but did not punch out. Please apply for regularization.
                  </p>
                )}
              </div>

              {/* Revoke Request Section */}
              {(displayStatus === 'present' || displayStatus === 'half_day') && !hasMissedPunchOut && (() => {
                const existingRevoke = getRevokeForDate(day);
                if (existingRevoke) {
                  const revokeColors = {
                    pending: 'bg-yellow-50 border-yellow-200',
                    approved: 'bg-green-50 border-green-200',
                    rejected: 'bg-red-50 border-red-200',
                  };
                  const revokeTextColors = {
                    pending: 'text-yellow-700',
                    approved: 'text-green-700',
                    rejected: 'text-red-700',
                  };
                  return (
                    <div className={`border rounded-xl p-4 ${revokeColors[existingRevoke.status]}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <RotateCcw className={`w-4 h-4 ${revokeTextColors[existingRevoke.status]}`} />
                        <span className={`text-sm font-semibold ${revokeTextColors[existingRevoke.status]}`}>
                          Revoke Request — {existingRevoke.status.charAt(0).toUpperCase() + existingRevoke.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{existingRevoke.reason}</p>
                      {existingRevoke.reviewComments && (
                        <p className="text-xs text-gray-500 mt-1 italic">"{existingRevoke.reviewComments}"</p>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    onClick={() => openRevokeModal(attendanceData._id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-xl text-sm font-medium transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Request Attendance Revoke
                  </button>
                );
              })()}

              {/* Punch In */}
              {attendanceData.punchIn?.time && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <Clock className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Punch In</p>
                      {attendanceData.punchIn.location?.latitude ? (
                        <button
                          onClick={() => window.open(`https://www.google.com/maps?q=${attendanceData.punchIn.location.latitude},${attendanceData.punchIn.location.longitude}`, '_blank', 'noopener,noreferrer')}
                          className="text-xl font-bold text-green-900 mt-0.5 hover:text-green-600 hover:underline cursor-pointer flex items-center gap-1"
                          title="View punch-in location on Google Maps"
                        >
                          {new Date(attendanceData.punchIn.time).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Asia/Kolkata' 
                          })}
                          <MapPin className="w-4 h-4" />
                        </button>
                      ) : (
                        <p className="text-xl font-bold text-green-900 mt-0.5">
                          {new Date(attendanceData.punchIn.time).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Asia/Kolkata' 
                          })}
                        </p>
                      )}
                      {attendanceData.punchIn.location && (
                        <p className="text-xs text-green-600 flex items-center gap-1 mt-2 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {attendanceData.punchIn.location.latitude?.toFixed(4)}, {attendanceData.punchIn.location.longitude?.toFixed(4)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Punch Out */}
              {attendanceData.punchOut?.time && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                      <Clock className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Punch Out</p>
                      {attendanceData.punchOut.location?.latitude ? (
                        <button
                          onClick={() => window.open(`https://www.google.com/maps?q=${attendanceData.punchOut.location.latitude},${attendanceData.punchOut.location.longitude}`, '_blank', 'noopener,noreferrer')}
                          className="text-xl font-bold text-red-900 mt-0.5 hover:text-red-600 hover:underline cursor-pointer flex items-center gap-1"
                          title="View punch-out location on Google Maps"
                        >
                          {new Date(attendanceData.punchOut.time).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Asia/Kolkata' 
                          })}
                          <MapPin className="w-4 h-4" />
                        </button>
                      ) : (
                        <p className="text-xl font-bold text-red-900 mt-0.5">
                          {new Date(attendanceData.punchOut.time).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true,
                            timeZone: 'Asia/Kolkata' 
                          })}
                        </p>
                      )}
                      {attendanceData.punchOut.location && (
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-2 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {attendanceData.punchOut.location.latitude?.toFixed(4)}, {attendanceData.punchOut.location.longitude?.toFixed(4)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Work Duration */}
              {attendanceData.workDuration > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Work Duration</p>
                      {attendanceData.overtime > 0 && (
                        <p className="text-xs text-indigo-500 mt-1">
                          +{Math.floor(attendanceData.overtime / 60)}h {attendanceData.overtime % 60}m OT
                        </p>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-indigo-700">
                      {Math.floor(attendanceData.workDuration / 60)}h {attendanceData.workDuration % 60}m
                    </span>
                  </div>
                </div>
              )}

              {/* Warnings */}
              {(attendanceData.isLateArrival || attendanceData.isEarlyDeparture) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  {attendanceData.isLateArrival && (
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium">Late Arrival</span>
                    </div>
                  )}
                  {attendanceData.isEarlyDeparture && (
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium">Early Departure</span>
                    </div>
                  )}
                </div>
              )}

              {/* Violations */}
              {attendanceData.violations?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-2">
                    Location Violations ({attendanceData.violations.length})
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {attendanceData.violations.slice(0, 5).map((violation, idx) => (
                      <div key={idx} className="text-xs text-red-700 bg-white p-2 rounded-lg">
                        <p className="font-medium">
                          {new Date(violation.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                        </p>
                        <p className="text-red-600 truncate">{violation.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* No attendance data */
            <div className="flex flex-col items-center justify-center py-8 text-center">
              {isBeforeStartDate ? (
                <div className="text-slate-400">
                  <UserX className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">Not Yet Joined</p>
                  <p className="text-xs text-slate-400 mt-1">Before employment start date</p>
                </div>
              ) : holidayData ? (
                <div className="text-purple-500">
                  <Star className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">Holiday</p>
                  <p className="text-xs text-purple-400 mt-1">No attendance required</p>
                </div>
              ) : isWeekend ? (
                <div className="text-slate-400">
                  <Coffee className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">Weekend</p>
                  <p className="text-xs text-slate-400 mt-1">Day off</p>
                </div>
              ) : isFutureDate ? (
                <div className="text-slate-400">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">Upcoming</p>
                  <p className="text-xs text-slate-400 mt-1">Future date</p>
                </div>
              ) : (
                <div className="text-red-400">
                  <XCircle className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">No Attendance</p>
                  <p className="text-xs text-red-400 mt-1">Marked as absent</p>
                  
                  {/* Check for existing regularization */}
                  {(() => {
                    const existingReg = getRegularizationForDate(day);
                    if (existingReg) {
                      // Show existing regularization info
                      return (
                        <div className="mt-4 p-4 bg-white border rounded-xl text-left w-full">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Regularization Applied</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              existingReg.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              existingReg.status === 'approved' ? 'bg-green-100 text-green-800' :
                              existingReg.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              existingReg.status === 'lop' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {existingReg.status === 'pending' && <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />}
                              {existingReg.status === 'approved' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                              {existingReg.status === 'rejected' && <Ban className="w-3 h-3 inline mr-1" />}
                              {existingReg.status?.charAt(0).toUpperCase() + existingReg.status?.slice(1)}
                            </span>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Type:</span>
                              <span className="text-gray-900 font-medium capitalize">{existingReg.type?.replace(/_/g, ' ')}</span>
                            </div>
                            {existingReg.requestedPunchIn && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">In Time:</span>
                                <span className="text-green-600 font-medium">
                                  {new Date(existingReg.requestedPunchIn).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                                </span>
                              </div>
                            )}
                            {existingReg.requestedPunchOut && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Out Time:</span>
                                <span className="text-red-600 font-medium">
                                  {new Date(existingReg.requestedPunchOut).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                                </span>
                              </div>
                            )}
                            <div className="pt-2 border-t">
                              <span className="text-gray-500 text-xs">Reason:</span>
                              <p className="text-gray-700 text-xs mt-1">{existingReg.reason}</p>
                            </div>
                            {existingReg.reviewComments && (
                              <div className="pt-2 border-t">
                                <span className="text-gray-500 text-xs">Admin Comments:</span>
                                <p className="text-gray-700 text-xs mt-1">{existingReg.reviewComments}</p>
                              </div>
                            )}
                            {existingReg.isLOP && (
                              <div className="pt-2 border-t bg-red-50 -mx-4 -mb-4 px-4 py-2 rounded-b-xl">
                                <span className="text-red-600 text-xs font-medium flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Loss of Pay Applied
                                  {existingReg.lopDeduction > 0 && ` - ₹${existingReg.lopDeduction.toLocaleString()}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    } else {
                      // Show Apply Regularization button
                      return (
                        <button
                          onClick={() => openRegularizationModal(day)}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto text-sm font-medium"
                        >
                          <FileEdit className="w-4 h-4" />
                          Apply Regularization
                        </button>
                      );
                    }
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Calculate summary stats
  const stats = useMemo(() => {
    const present = attendance.filter(a => a.status === 'present').length;
    const halfDays = attendance.filter(a => a.status === 'half_day').length;
    const leaves = attendance.filter(a => a.status === 'leave').length;
    const lateArrivals = attendance.filter(a => a.isLateArrival).length;
    const totalHours = Math.floor(attendance.reduce((sum, a) => sum + (a.workDuration || 0), 0) / 60);
    const extraDays = extraWorkDays.length;
    
    // Calculate absent days: past working days without attendance records
    let absentCount = attendance.filter(a => a.status === 'absent').length;
    
    // Also count days that are past working days but have no attendance record
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(currentYear, currentMonth, day);
      dateObj.setHours(0, 0, 0, 0);
      const dayOfWeek = dateObj.getDay();
      const isWeekend = isOffDay(dayOfWeek);
      const isPastDate = dateObj < today;
      const isBeforeStartDate = startDate && dateObj < startDate;
      
      // Check if there's attendance for this day
      const hasAttendance = attendance.some(a => {
        const attendanceDate = new Date(a.date);
        attendanceDate.setHours(0, 0, 0, 0);
        return attendanceDate.getTime() === dateObj.getTime();
      });
      
      // Check if there's a holiday for this day
      const hasHoliday = holidays.some(h => {
        const holidayDate = new Date(h.date);
        holidayDate.setHours(0, 0, 0, 0);
        return holidayDate.getTime() === dateObj.getTime();
      });
      
      // Count as absent if: past date, working day (not weekend), no attendance, no holiday, after start date
      if (isPastDate && !isWeekend && !hasAttendance && !hasHoliday && !isBeforeStartDate) {
        absentCount++;
      }
    }

    return { present, absent: absentCount, halfDays, leaves, lateArrivals, totalHours, extraDays };
  }, [attendance, extraWorkDays, currentMonth, currentYear, holidays, startDate, workingDays]);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col lg:flex-row h-full">
        {/* Main Calendar Section - 75% width on large screens */}
        <div className="flex-1 lg:w-3/4 p-4 sm:p-6 border-b lg:border-b-0 lg:border-r border-slate-200">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-xl">
                <CalendarIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Attendance Calendar</h2>
                <p className="text-xs text-slate-500">{currentYear}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousMonth}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>

              <div className="text-center min-w-[140px] px-4 py-2 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-sm font-bold text-slate-900">{monthNames[currentMonth]}</div>
              </div>

              <button
                onClick={handleNextMonth}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200"
                aria-label="Next month"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>

              <button
                onClick={handleToday}
                className="ml-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm"
              >
                Today
              </button>
            </div>
          </div>

          {/* Shift Info Display */}
          {shiftInfo && (
            <div className="flex flex-wrap items-center gap-3 mb-5 pb-4 border-b border-slate-200">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-lg border border-indigo-200">
                <Clock className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-700">
                  {shiftInfo.name || 'Shift'}: {shiftInfo.startTime} - {shiftInfo.endTime}
                </span>
              </div>
              {shiftInfo.gracePeriodMinutes > 0 && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 rounded-lg border border-amber-200">
                  <span className="text-xs text-amber-700">Grace: {shiftInfo.gracePeriodMinutes} min</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-xs text-blue-700">Min Hours: {shiftInfo.minimumWorkHours}h</span>
              </div>
              {shiftInfo.isNightShift && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded-lg border border-purple-200">
                  <span className="text-xs text-purple-700">🌙 Night Shift</span>
                </div>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-5 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-slate-600 font-medium">Present</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-slate-600 font-medium">Absent</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-xs text-slate-600 font-medium">Half Day</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-slate-600 font-medium">Leave</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-slate-300"></div>
              <span className="text-xs text-slate-600 font-medium">Weekend</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-xs text-slate-600 font-medium">Holiday</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-emerald-300"></div>
              <span className="text-xs text-emerald-700 font-medium">Extra Work</span>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-3 h-3 rounded-full bg-green-500 relative">
                <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
              </div>
              <span className="text-xs text-orange-700 font-medium">Late</span>
            </div>
            {employeeStartDate && (
              <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <div className="w-3 h-3 rounded-full bg-slate-200 border border-dashed border-slate-300"></div>
                <span className="text-xs text-slate-500 font-medium">Not Joined</span>
              </div>
            )}
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-slate-600">Loading calendar...</span>
            </div>
          ) : (
            <div className="calendar-container">
              {renderCalendar()}
            </div>
          )}

          {/* Summary Stats */}
          {!loading && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                  <div className="text-2xl font-bold text-green-600">{stats.present}</div>
                  <div className="text-xs text-green-700 font-medium">Present</div>
                </div>
                <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
                  <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
                  <div className="text-xs text-red-700 font-medium">Absent</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                  <div className="text-2xl font-bold text-amber-600">{stats.halfDays}</div>
                  <div className="text-xs text-amber-700 font-medium">Half Days</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                  <div className="text-2xl font-bold text-blue-600">{stats.leaves}</div>
                  <div className="text-xs text-blue-700 font-medium">Leaves</div>
                </div>
                <div className="bg-orange-50 rounded-xl p-3 text-center border border-orange-100">
                  <div className="text-2xl font-bold text-orange-600">{stats.lateArrivals}</div>
                  <div className="text-xs text-orange-700 font-medium">Late</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                  <div className="text-2xl font-bold text-emerald-600">{stats.extraDays}</div>
                  <div className="text-xs text-emerald-700 font-medium">Extra Days</div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
                  <div className="text-2xl font-bold text-indigo-600">{stats.totalHours}h</div>
                  <div className="text-xs text-indigo-700 font-medium">Total Hours</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Details Panel - 25% width on large screens */}
        <div className="lg:w-1/4 min-w-[280px] bg-slate-50 lg:min-h-[600px]">
          {renderDetailsPanel()}
        </div>
      </div>

      {/* Regularization Modal */}
      {showRegularizationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileEdit className="w-6 h-6" />
                  <div>
                    <h3 className="text-lg font-bold">Apply for Regularization</h3>
                    <p className="text-xs text-blue-100">{regularizationForm.date ? new Date(regularizationForm.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRegularizationModal(false)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleRegularizationSubmit} className="p-6 space-y-4">
              {regularizationError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {regularizationError}
                </div>
              )}
              
              {regularizationSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {regularizationSuccess}
                </div>
              )}

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Regularization Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={regularizationForm.type}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {regularizationTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Punch In Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requested Punch In Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={regularizationForm.requestedPunchIn}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, requestedPunchIn: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Punch Out Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requested Punch Out Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={regularizationForm.requestedPunchOut}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, requestedPunchOut: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Live work-hours preview */}
              {regularizationForm.requestedPunchIn && regularizationForm.requestedPunchOut && (() => {
                const [ph, pm] = regularizationForm.requestedPunchIn.split(':').map(Number);
                const [oh, om] = regularizationForm.requestedPunchOut.split(':').map(Number);
                const mins = (oh * 60 + om) - (ph * 60 + pm);
                if (mins <= 0) {
                  return (
                    <p className="text-xs text-red-600 flex items-center gap-1 -mt-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Punch Out must be after Punch In
                    </p>
                  );
                }
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                return (
                  <p className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5 -mt-1">
                    Work duration: <strong>{h}h {m}m</strong>
                  </p>
                );
              })()}

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={regularizationForm.reason}
                  onChange={(e) => setRegularizationForm(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder="Please provide a detailed reason for this regularization request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span><strong>Note:</strong> If this request is rejected, the day will be marked as Loss of Pay (LOP).</span>
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRegularizationModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={regularizationLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {regularizationLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6" />
                <h3 className="text-lg font-bold">Request Attendance Revoke</h3>
              </div>
              <button onClick={() => setShowRevokeModal(false)} className="p-1.5 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleRevokeSubmit} className="p-6 space-y-4">
              {revokeError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {revokeError}
                </div>
              )}
              {revokeSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {revokeSuccess}
                </div>
              )}

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>If approved by your manager, this attendance will be reset to <strong>Absent</strong>.</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why you want to revoke this attendance..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRevokeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={revokeLoading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {revokeLoading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
                  ) : (
                    <><RotateCcw className="w-4 h-4" />Submit Request</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AttendanceCalendar;
